/**
 * Thin wrappers around the orval-generated settings hooks: the team and
 * its members, the review-scan controls, and the notification callback.
 *
 * Same unwrap pattern as useApiKeys: the generated hooks return
 * `{data, status, headers}`, and each write invalidates the read it
 * changes so the Settings sections never repeat queryKey plumbing.
 */

import { useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import { getApiErrorDetail, type ApiRequestError } from '@/api/client'
import {
  getGetDreamScanApiSettingsTeamDreamScanGetQueryKey,
  useGetJobMapApiSettingsTeamJobMapGet,
  getGetNotificationSettingsApiSettingsTeamNotificationsGetQueryKey,
  getGetTeamInfoApiSettingsTeamGetQueryKey,
  getGetTrainLoopLinksApiSettingsTeamTrainLoopLinksGetQueryKey,
  type deleteTeamApiSettingsTeamsTeamIdDeleteResponse,
  useDeleteTeamApiSettingsTeamsTeamIdDelete,
  useGetTeamDeletePreviewApiSettingsTeamsTeamIdDeletePreviewGet,
  useGetDreamScanApiSettingsTeamDreamScanGet,
  useGetDreamScanFieldValuesApiSettingsTeamDreamScanFieldValuesGet,
  useGetGithubAppStatusApiSettingsTeamGithubAppGet,
  useGetTrainLoopApiSettingsTeamTrainLoopGet,
  useGetTrainLoopLinksApiSettingsTeamTrainLoopLinksGet,
  useGetNotificationSettingsApiSettingsTeamNotificationsGet,
  useGetTeamInfoApiSettingsTeamGet,
  useInviteMemberApiSettingsTeamInvitePost,
  useRemoveMemberApiSettingsTeamMembersMemberIdDelete,
  useRunTrainLoopLinkApiSettingsTeamTrainLoopLinksSlugRunPost,
  useSetMemberPermissionsApiSettingsTeamMembersMemberIdPermissionsPut,
  useSetTrainLoopLinkThresholdApiSettingsTeamTrainLoopLinksSlugThresholdPut,
  useTestDreamScanRulesApiSettingsTeamDreamScanRulesTestPost,
  useTransferTeamOwnershipApiSettingsTeamMembersMemberIdTransferOwnershipPost,
  useUpdateDreamScanApiSettingsTeamDreamScanPut,
  useUpdateNotificationSettingsApiSettingsTeamNotificationsPut,
  type runTrainLoopLinkApiSettingsTeamTrainLoopLinksSlugRunPostResponse,
  type setTrainLoopLinkThresholdApiSettingsTeamTrainLoopLinksSlugThresholdPutResponse,
  type testDreamScanRulesApiSettingsTeamDreamScanRulesTestPostResponse,
} from '@/api/generated/settings/settings'
import {
  getGetTrainingRolloutCredentialsApiTrainingRunsRolloutCredentialsGetQueryKey,
  useGetTrainingRolloutCredentialsApiTrainingRunsRolloutCredentialsGet,
  useSetTrainingRolloutCredentialsApiTrainingRunsRolloutCredentialsPut,
} from '@/api/generated/training-runs/training-runs'
import type {
  DreamScanFieldValues,
  DreamScanRuleTestRequest,
  DreamScanSettings,
  GithubAppStatus,
  InviteMemberRequest,
  JobMapReport,
  MemberPermissionsRequest,
  TrainLoopLinkThreshold,
  TrainLoopLinks,
  TrainLoopStatus,
  NotificationSettings,
  TeamInfoResponse,
  TeamMemberResponse,
  TrainingRolloutCredentialsRequest,
  TrainingRolloutCredentialsResponse,
  UpdateDreamScanRequest,
  UpdateNotificationSettingsRequest,
} from '@/api/generated/model'
import type { TeamDeletePreviewState } from '@/components/shared/TeamDeleteDialog'

export type TeamMember = TeamMemberResponse

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

// ── Team and members ────────────────────────────────────────────────────

export function useTeamMembers(): UnwrappedQuery<TeamInfoResponse> {
  const query = useGetTeamInfoApiSettingsTeamGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

function useInvalidateTeamMembers(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: getGetTeamInfoApiSettingsTeamGetQueryKey(),
    })
  }
}

export function useInviteMember(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: InviteMemberRequest }
> {
  const invalidate = useInvalidateTeamMembers()
  return useInviteMemberApiSettingsTeamInvitePost({
    mutation: { onSuccess: invalidate },
  })
}

/** Remove a member. Leaving is the same call with the caller's own id. */
export function useRemoveMember(): UseMutationResult<
  unknown,
  ApiRequestError,
  { memberId: string }
> {
  const invalidate = useInvalidateTeamMembers()
  return useRemoveMemberApiSettingsTeamMembersMemberIdDelete({
    mutation: { onSuccess: invalidate },
  })
}

export function useTransferOwnership(): UseMutationResult<
  unknown,
  ApiRequestError,
  { memberId: string }
> {
  const invalidate = useInvalidateTeamMembers()
  return useTransferTeamOwnershipApiSettingsTeamMembersMemberIdTransferOwnershipPost({
    mutation: { onSuccess: invalidate },
  })
}

/** Set what one member can do beyond every member (TRA-1700). Owner only. */
export function useSetMemberPermissions(): UseMutationResult<
  unknown,
  ApiRequestError,
  { memberId: string; data: MemberPermissionsRequest }
> {
  const invalidate = useInvalidateTeamMembers()
  return useSetMemberPermissionsApiSettingsTeamMembersMemberIdPermissionsPut({
    mutation: { onSuccess: invalidate },
  })
}

/**
 * Delete a team the caller owns. The teams list lives in /api/auth/me
 * (TeamContext), so the caller refetches that or switches team.
 */
/**
 * What deleting the team would remove (TRA-1508), for the confirm dialog.
 * Fetched only while the dialog is open for that team, never retried: a
 * 403 or 404 is an answer, not a blip.
 */
export function useTeamDeletePreview(teamId: string | null): TeamDeletePreviewState {
  const query = useGetTeamDeletePreviewApiSettingsTeamsTeamIdDeletePreviewGet(teamId ?? '', {
    query: { enabled: teamId !== null, retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.isError ? (getApiErrorDetail(query.error) ?? 'The preview failed to load.') : null,
  }
}

/** Deletes the team; `confirm` is the team's name, which the api checks too. */
export function useDeleteTeam(): UseMutationResult<
  deleteTeamApiSettingsTeamsTeamIdDeleteResponse,
  ApiRequestError,
  { teamId: string; params: { confirm: string } }
> {
  return useDeleteTeamApiSettingsTeamsTeamIdDelete()
}

// ── Review scans ────────────────────────────────────────────────────────

export function useDreamScanSettings(): UnwrappedQuery<DreamScanSettings> {
  // A 404 means the nightly scan never visits this team; retrying it is noise.
  const query = useGetDreamScanApiSettingsTeamDreamScanGet({ query: { retry: false } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** The windows the job map offers, in the order its pills show them. */
export type JobMapWindow = 7 | 30

export function useJobMap(days: JobMapWindow): UnwrappedQuery<JobMapReport> {
  // A 404 means the team has no map yet; retrying it is noise.
  const query = useGetJobMapApiSettingsTeamJobMapGet({ days }, { query: { retry: false } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useDreamScanFieldValues(): UnwrappedQuery<DreamScanFieldValues> {
  const query = useGetDreamScanFieldValuesApiSettingsTeamDreamScanFieldValuesGet({
    query: { retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useUpdateDreamScan(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: UpdateDreamScanRequest }
> {
  const queryClient = useQueryClient()
  return useUpdateDreamScanApiSettingsTeamDreamScanPut({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetDreamScanApiSettingsTeamDreamScanGetQueryKey(),
        })
      },
    },
  })
}

/** Preview draft rules against observed traces. Writes nothing, so nothing to invalidate. */
export function useTestDreamScanRules(): UseMutationResult<
  testDreamScanRulesApiSettingsTeamDreamScanRulesTestPostResponse,
  ApiRequestError,
  { data: DreamScanRuleTestRequest }
> {
  return useTestDreamScanRulesApiSettingsTeamDreamScanRulesTestPost()
}

// ── Notifications ───────────────────────────────────────────────────────

export function useNotificationSettings(): UnwrappedQuery<NotificationSettings> {
  // A 404 means the team has no gateway credentials yet; retrying it is noise.
  const query = useGetNotificationSettingsApiSettingsTeamNotificationsGet({
    query: { retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useUpdateNotificationSettings(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: UpdateNotificationSettingsRequest }
> {
  const queryClient = useQueryClient()
  return useUpdateNotificationSettingsApiSettingsTeamNotificationsPut({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetNotificationSettingsApiSettingsTeamNotificationsGetQueryKey(),
        })
      },
    },
  })
}

// ── GitHub App ──────────────────────────────────────────────────────────

export interface GithubAppStatusQuery extends UnwrappedQuery<GithubAppStatus> {
  /** True while a Check again is in flight; the last answer stays on screen. */
  isFetching: boolean
  refetch: () => void
}

/**
 * The team's installation of the TrainLoop GitHub App, asked of GitHub on
 * every read. No retry: a missing or unreachable installation comes back
 * as a status the page renders, and a failed request is retried by the
 * page's own Check again.
 */
export function useGithubAppStatus(): GithubAppStatusQuery {
  const query = useGetGithubAppStatusApiSettingsTeamGithubAppGet({ query: { retry: false } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => {
      void query.refetch()
    },
  }
}

// ── Pipeline ──────────────────────────────────────────────────────────

/**
 * Every stage of the train loop and whether it runs for this team.
 * Always refetched: every input is edited on another page (Review scans,
 * Models, GitHub App, the staff Evals config) and none of those writes
 * invalidates this read, so a cached answer would show the old states.
 */
export function useTrainLoopStatus(): UnwrappedQuery<TrainLoopStatus> {
  const query = useGetTrainLoopApiSettingsTeamTrainLoopGet({ query: { staleTime: 0 } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * The links of the pipeline chain for this team (TRA-1354): what starts
 * each job, what waits now, and its last run. Not retried: a stack
 * outside Kubernetes answers 503 on every read, and the page shows that.
 */
/** How often the links are read again while one of them is running. */
const RUNNING_LINK_POLL_MS = 10_000

export function useTrainLoopLinks(): UnwrappedQuery<TrainLoopLinks> {
  const query = useGetTrainLoopLinksApiSettingsTeamTrainLoopLinksGet({
    query: {
      staleTime: 0,
      retry: false,
      // A running Job ends on its own; the page follows it without a reload.
      refetchInterval: (state) =>
        state.state.data?.status === 200 &&
        state.state.data.data.links.some((link) => link.active_job_name !== null)
          ? RUNNING_LINK_POLL_MS
          : false,
    },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** Start one link now. Settled either way, the links are read again. */
export function useRunTrainLoopLink(): UseMutationResult<
  runTrainLoopLinkApiSettingsTeamTrainLoopLinksSlugRunPostResponse,
  ApiRequestError,
  { slug: string }
> {
  const queryClient = useQueryClient()
  return useRunTrainLoopLinkApiSettingsTeamTrainLoopLinksSlugRunPost({
    mutation: {
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTrainLoopLinksApiSettingsTeamTrainLoopLinksGetQueryKey(),
        })
      },
    },
  })
}

/** Set how much work a link needs waiting before its event starts the job. */
export function useSetTrainLoopLinkThreshold(): UseMutationResult<
  setTrainLoopLinkThresholdApiSettingsTeamTrainLoopLinksSlugThresholdPutResponse,
  ApiRequestError,
  { slug: string; data: TrainLoopLinkThreshold }
> {
  const queryClient = useQueryClient()
  return useSetTrainLoopLinkThresholdApiSettingsTeamTrainLoopLinksSlugThresholdPut({
    mutation: {
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTrainLoopLinksApiSettingsTeamTrainLoopLinksGetQueryKey(),
        })
      },
    },
  })
}

// ── Rollout secrets (TRA-1215) ──────────────────────────────────────────

export function useRolloutSecrets(): UnwrappedQuery<TrainingRolloutCredentialsResponse> {
  // A 400 means the team has no gateway slug yet; retrying it is noise.
  const query = useGetTrainingRolloutCredentialsApiTrainingRunsRolloutCredentialsGet({
    query: { retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useUpdateRolloutSecrets(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TrainingRolloutCredentialsRequest }
> {
  const queryClient = useQueryClient()
  return useSetTrainingRolloutCredentialsApiTrainingRunsRolloutCredentialsPut({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTrainingRolloutCredentialsApiTrainingRunsRolloutCredentialsGetQueryKey(),
        })
      },
    },
  })
}
